import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Modal,
  Box,
  Button,
  LinearProgress,
  TextField,
  IconButton,
} from '@material-ui/core';
import { Close, KeyboardArrowRight } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import ContentCopy from '../../images/ContentCopy.svg';
import ShackBar from '../parts/SnackBar/SnackBar';
import CommonTable from './CommonTable';
import { LiveEncoderV3Props } from '../../props/LiveEncoder';
import { RootState } from '../../app/store';
import { UI_STATUS, SUPPORTPAGE_URL_LIVEENCODER } from '../../utils/Const';

const CustomButtomView = ({
  title,
  onClick,
  className,
}: {
  title: string;
  className: string;
  onClick?: () => void;
}): JSX.Element => {
  return (
    <Button className={className} onClick={onClick}>
      {title}
      <KeyboardArrowRight
        fontSize="small"
        style={{ marginLeft: 4, width: 16, height: 16 }}
      />
    </Button>
  );
};

const CopyButtomView = ({
  onClick,
  className,
}: {
  className: string;
  onClick?: () => void;
}): JSX.Element => {
  return (
    <IconButton className={className} onClick={onClick}>
      <img src={ContentCopy} alt="ContentCopy" />
    </IconButton>
  );
};

/** スタイル */
const useStyle = makeStyles({
  root: {
    width: 980,
    margin: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#333333',
    '&:focus-visible': {
      outline: 'none',
    },
  },
  container: {
    width: '100%',
    height: 'calc(100vh - 520px + 308px + 64px)',
    background: '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0px 4px 16px #0000001F',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 30px',
    alignItems: 'center',
    borderBottom: '1px solid #E0E0E0',
    fontSize: 20,
  },
  body: {
    padding: '30px 40px',
    fontSize: 15,
    height: 'calc(100% - 140px)',
    overflow: 'hidden auto',
    '& h4': {
      fontSize: 15,
      fontWeight: 500,
      borderLeft: '2px solid #61B227',
      paddingLeft: 20,
      marginTop: 31,
    },
    '& .MuiTableCell-head': {
      padding: '18px 25px',
    },
    '& .MuiTableCell-body': {
      padding: '18px 25px',
    },
  },
  button: {
    background: '#D8D8D8',
    borderBottom: '1px solid #AAAAAA',
    borderRadius: 4,
    fontWeight: 500,
    fontSize: 14,
    color: '#333333',
    width: 154,
    '&:hover': {
      background: '#333333',
      color: '#FFFFFF',
    },
  },
  settingTextField: {
    lineHeight: '18px',
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: 50,
      background: '#FAFAFA',
      borderRadius: 4,
      fontSize: 13,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#DDDDDD',
    },
    '& .MuiOutlinedInput-input': {
      whiteSpace: 'nowrap',
      width: '90%',
      textOverflow: 'ellipsis',
    },
  },
  streamName: {
    width: '230px',
  },
  iconButton: {
    borderRadius: 60,
    color: '#333333',
    position: 'absolute',
    right: '6px',
    top: 0,
    bottom: 0,
    width: '40px',
    height: '40px',
    margin: 'auto',
    '&:hover': {
      background: '#D8D8D8',
    },
  },
});

type propsType = {
  open: boolean;
  props: LiveEncoderV3Props;
  onClose: () => void;
};

/** ライブエンコーダー設定(UI) */
const LiveEncoderV3Component = ({
  open,
  props,
  onClose,
}: propsType): JSX.Element => {
  const classes = useStyle();
  const status = useSelector((state: RootState) => state.ui.status);
  const {
    lpid,
    liveName,
    streamParams,
    downloadEncorderTextProfile,
    copySettingValue,
    handleSnackbarClose,
    snackbarInfo,
  } = props;

  return (
    <Modal
      open={open}
      className={classes.root}
      onClose={onClose}
      BackdropProps={{
        style: {
          backgroundColor: '#00000033',
        },
      }}
    >
      {status.liveEncoder === UI_STATUS.PROCESSING ? (
        <Box className={classes.container}>
          <LinearProgress />
        </Box>
      ) : (
        <Box className={classes.container}>
          <Box className={classes.header}>
            <h3>ライブエンコーダー設定取得</h3>
            <Close onClick={onClose} style={{ cursor: 'pointer' }} />
          </Box>
          <Box className={classes.body}>
            <Box>
              ライブエンコーダーに設定する設定情報を取得することができます。
            </Box>
            <h4 style={{ marginTop: 20 }}>
              ライブエンコーダー設定のダウンロード
            </h4>
            <CommonTable
              header={[
                {
                  name: 'liveEncoder',
                  title: '項目',
                  width: 750,
                },
                {
                  name: 'download',
                  title: (
                    <Box style={{ textAlign: 'center' }}>ダウンロード</Box>
                  ),
                  width: 150,
                },
              ]}
              body={[
                [
                  {
                    name: 'liveEncoder',
                    content: 'ライブエンコーダー設定のダウンロード',
                  },
                  {
                    name: 'download',
                    content: (
                      <CustomButtomView
                        title="ダウンロード"
                        onClick={downloadEncorderTextProfile}
                        className={classes.button}
                      />
                    ),
                  },
                ],
              ]}
            />
            <h4>ライブエンコーダー設定内容</h4>
            <p style={{ marginBottom: 22 }}>
              上部のダウンロードで取得できるテキストファイルと同等の情報です。
            </p>
            <CommonTable
              header={[
                { name: 'item', title: '項目', width: 280 },
                { name: 'settingValue', title: '設定値', width: 620 },
              ]}
              body={[
                [
                  { name: 'item', content: 'ライブ設定ID' },
                  { name: 'settingValue', content: lpid },
                ],
                [
                  { name: 'item', content: 'ライブ設定名' },
                  { name: 'settingValue', content: liveName },
                ],
                [
                  { name: 'item', content: 'メインエンコーダーURL' },
                  {
                    name: 'settingValue',
                    content: (
                      <Box style={{ position: 'relative', left: '-13px' }}>
                        <TextField
                          value={streamParams?.feedUrl?.main}
                          InputProps={{ readOnly: true }}
                          className={classes.settingTextField}
                          rows={2}
                          variant="outlined"
                          id="mainUrlSettingTextField"
                          onFocus={(e) => e.target.select()}
                          onClick={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.select();
                          }}
                        />
                        <CopyButtomView
                          onClick={() =>
                            copySettingValue('mainUrlSettingTextField')
                          }
                          className={classes.iconButton}
                        />
                      </Box>
                    ),
                  },
                ],
                [
                  { name: 'item', content: 'バックアップエンコーダーURL' },
                  {
                    name: 'settingValue',
                    content: (
                      <Box style={{ position: 'relative', left: '-13px' }}>
                        <TextField
                          value={streamParams?.feedUrl?.backup}
                          InputProps={{ readOnly: true }}
                          className={classes.settingTextField}
                          rows={2}
                          variant="outlined"
                          id="backupUrlSettingTextField"
                          onFocus={(e) => e.target.select()}
                          onClick={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.select();
                          }}
                        />
                        <CopyButtomView
                          onClick={() =>
                            copySettingValue('backupUrlSettingTextField')
                          }
                          className={classes.iconButton}
                        />
                      </Box>
                    ),
                  },
                ],
                [
                  { name: 'item', content: 'ストリームキー' },
                  {
                    name: 'settingValue',
                    content: (
                      <Box
                        style={{ position: 'relative', left: '-13px' }}
                        className={classes.streamName}
                      >
                        <TextField
                          value={streamParams?.streamName}
                          InputProps={{ readOnly: true }}
                          className={classes.settingTextField}
                          rows={2}
                          variant="outlined"
                          id="streamNameSettingTextField"
                          onFocus={(e) => e.target.select()}
                          onClick={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.select();
                          }}
                        />
                        <CopyButtomView
                          onClick={() =>
                            copySettingValue('streamNameSettingTextField')
                          }
                          className={classes.iconButton}
                        />
                      </Box>
                    ),
                  },
                ],
                [
                  { name: 'item', content: 'エンコーダー映像設定' },
                  {
                    name: 'settingValue',
                    content: `${streamParams?.qualityText}(${streamParams?.video?.resolutionHeight}p, ${streamParams?.video?.bitrate}kbps)`,
                  },
                ],
                [
                  { name: 'item', content: 'フレームレート' },
                  {
                    name: 'settingValue',
                    content: `${streamParams?.video?.framerate} fps`,
                  },
                ],
                [
                  { name: 'item', content: 'キーフレーム間隔' },
                  {
                    name: 'settingValue',
                    content: `${streamParams?.video?.keyframe} フレームごと（1秒ごと）`,
                  },
                ],
                [
                  { name: 'item', content: 'エンコーダー音声設定' },
                  {
                    name: 'settingValue',
                    content: `${streamParams?.audio?.bitrate}kbps( ${streamParams?.audio?.samplingRate[0]} または ${streamParams?.audio?.samplingRate[1]}kHz)`,
                  },
                ],
              ]}
            />
            <p style={{ marginBottom: 22 }}>
              詳しい設定は、
              <a
                href={SUPPORTPAGE_URL_LIVEENCODER}
                target="_blank"
                rel="noreferrer"
              >
                カスタマーサイトをご確認ください。
              </a>
            </p>
          </Box>
          <ShackBar
            infomations={snackbarInfo}
            handleClose={handleSnackbarClose}
          />
        </Box>
      )}
    </Modal>
  );
};

export default LiveEncoderV3Component;
